import {
  PaginationSort,
  GetAllClaimInsuredQuery,
  GetAllClaimInsuredQueryVariables,
} from '../gql/graphql';
import { getPaginatedNodes, toPagination } from './utils';
import { useEffect, useMemo } from 'react';
import { graphql } from '../gql';
import { useQuery } from '@apollo/client';

export interface InsuredOption {
  label: string;
  value: string;
}

export const GET_ALL_CLAIM_INSURED = graphql(`
  query GetAllClaimInsured(
    $pagination: PaginationRequest
    $sort: PaginationSort
    $filters: EntityFilterInput!
  ) {
    entities(pagination: $pagination, sort: $sort, filters: $filters) {
      edges {
        cursor
        node {
          uuid
          aliases(filters: { source: PRIMARY, type: LEGAL_NAME }) {
            edges {
              node {
                uuid
                name
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);

export function useGetAllClaimQuery({
  pageNumber = 0,
  pageSize = 50,
  enabled = true,
  sort,
  insuredName,
}: {
  pageNumber?: number;
  pageSize?: number;
  enabled?: boolean;
  sort?: PaginationSort;
  insuredName?: string;
}) {
  const variables = {
    pagination: toPagination(pageNumber, pageSize),
    sort: sort || { key: 'PRIMARY_LEGAL_NAME', order: 'ASC' },
    filters: {
      contains: true,
      claimInsured: true,
      ...(insuredName && { alias: insuredName }),
    },
  };

  const { loading, error, data, refetch, fetchMore } = useQuery<
    GetAllClaimInsuredQuery,
    GetAllClaimInsuredQueryVariables
  >(GET_ALL_CLAIM_INSURED, {
    variables,
    skip: !enabled,
    fetchPolicy: 'network-only',
  });

  const insureds = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) return [];

    const insuredOptions: InsuredOption[] = [];

    getPaginatedNodes(data.entities).forEach((entity) => {
      if (entity?.uuid) {
        const aliases = getPaginatedNodes(entity.aliases || { edges: [] });

        const label = aliases[0]?.name;

        // Only add if we have a name from alias
        if (label) {
          insuredOptions.push({
            value: entity.uuid,
            label: label,
          });
        }
      }
    });

    return insuredOptions;
  }, [data]);

  const pageInfo = data?.entities?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const totalInsureds = pageInfo?.totalResults ?? 0;

  useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  return {
    error,
    loading,
    data,
    insureds,
    refetch,
    fetchMore,
    hasNextPage,
    pageInfo,
    totalInsureds,
  };
}
